import React, { useState } from 'react';
import AttachmentPreviewModal from '@/components/AttachmentPreviewModal';
import type { PreviewableAttachment } from '@/utils/attachmentPreview';
import {
  ArrowLeft,
  Reply,
  ReplyAll,
  Forward,
  Trash2,
  Star,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Download,
  Printer,
  FileSearch,
  Code,
  Copy,
  Loader2,
} from 'lucide-react';
import AttachmentItem from '@/components/AttachmentItem';
import EmailRepliesThread from './EmailRepliesThread';
import { getRenderableBody, type EmailBody, type EmailBodyLike } from '@/utils/emailBody';
import { EmailReply } from '@/hooks/useEmailReplies';
import './EmailView.css';

interface Recipient {
  name?: string | null;
  mailbox: string;
  host: string;
}

interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  public_url?: string;
}

interface EmailViewPanelProps {
  email: {
    id: string;
    uid: number | null;
    replyCount?: number;
    flags: string[];
    envelope: {
      date: string;
      subject: string;
      from: Recipient[];
      to: Recipient[];
      cc?: Recipient[] | null;
      bcc?: Recipient[] | null;
    };
    body: EmailBody & { html: string; text: string };
    attachments: Attachment[];
  };
  isMobile: boolean;
  isStarred: boolean;
  senderName: string;
  sender: Recipient;
  initials: string;
  avatarColor: string;
  showMoreActions: boolean;
  setShowMoreActions: (show: boolean) => void;
  isSendingCopy: boolean;
  isShowingOriginal: boolean;
  isViewing: boolean;
  isPrinting: boolean;
  isDownloading: boolean;
  replies: EmailReply[];
  repliesLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  formatDate: (date: string) => string;
  formatFileSize: (bytes: number) => string;
  resizeIframe: () => void;
  onBack: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onStar: () => void;
  onDelete: () => void;
  onSendCopy: () => void;
  onShowOriginal: () => void;
  onViewHtml: () => void;
  onPrint: () => void;
  onDownload: () => void;
  onAttachmentDownload: (attachment: PreviewableAttachment, progress: (value: number) => void) => Promise<void>;
  fetchAttachmentBlob: (
    attachment: PreviewableAttachment,
    onProgress?: (value: number) => void
  ) => Promise<Blob>;
  bodyFallback?: EmailBodyLike;
  totalReplies?: number;
}

function formatRecipient(recipient: Recipient) {
  const address = `${recipient.mailbox}@${recipient.host}`;
  return recipient.name ? `${recipient.name} <${address}>` : address;
}

function formatRecipientsList(recipients: Recipient[]) {
  return recipients.map(formatRecipient).join(', ');
}

function formatDateShort(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const EmailViewPanel: React.FC<EmailViewPanelProps> = ({
  email,
  isMobile,
  isStarred,
  senderName,
  sender,
  initials,
  avatarColor,
  showMoreActions,
  setShowMoreActions,
  isSendingCopy,
  isShowingOriginal,
  isViewing,
  isPrinting,
  isDownloading,
  replies,
  repliesLoading,
  hasMore,
  loadMore,
  iframeRef,
  formatDate,
  formatFileSize,
  resizeIframe,
  onBack,
  onReply,
  onReplyAll,
  onForward,
  onStar,
  onDelete,
  onSendCopy,
  onShowOriginal,
  onViewHtml,
  onPrint,
  onDownload,
  onAttachmentDownload,
  fetchAttachmentBlob,
  bodyFallback,
  totalReplies,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<PreviewableAttachment | null>(null);

  const openAttachmentPreview = (attachment: PreviewableAttachment) => {
    setPreviewAttachment(attachment);
  };
  const renderable = getRenderableBody(email.body);
  const senderAddress = `${sender.mailbox}@${sender.host}`;
  const primaryRecipient = email.envelope.to?.[0];
  const recipientSummary = primaryRecipient
    ? primaryRecipient.name || `${primaryRecipient.mailbox}@${primaryRecipient.host}`
    : 'me';

  const closeMoreMenu = () => setShowMoreActions(false);

  return (
    <div className="gmail-view">
      <div className="gmail-view__toolbar">
        <div className="gmail-view__toolbar-group">
          <button type="button" className="gmail-view__icon-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        </div>

        <div className="gmail-view__toolbar-divider" />

        <div className="gmail-view__toolbar-group">
          <button
            type="button"
            className={`gmail-view__icon-btn ${isStarred ? 'gmail-view__icon-btn--starred' : ''}`}
            onClick={onStar}
            aria-label={isStarred ? 'Unstar' : 'Star'}
          >
            <Star size={20} fill={isStarred ? 'currentColor' : 'none'} />
          </button>
          <button type="button" className="gmail-view__icon-btn" onClick={onReply} aria-label="Reply">
            <Reply size={20} />
          </button>
          {!isMobile && (
            <button type="button" className="gmail-view__icon-btn" onClick={onReplyAll} aria-label="Reply all">
              <ReplyAll size={20} />
            </button>
          )}
          <button type="button" className="gmail-view__icon-btn" onClick={onForward} aria-label="Forward">
            <Forward size={20} />
          </button>
        </div>

        <div className="gmail-view__toolbar-divider" />

        <div className="gmail-view__toolbar-group">
          <button type="button" className="gmail-view__icon-btn" onClick={onDelete} aria-label="Delete">
            <Trash2 size={20} />
          </button>
        </div>

        <div style={{ marginLeft: 'auto' }} className="gmail-view__more-dropdown">
          <button
            type="button"
            className={`gmail-view__icon-btn ${showMoreActions ? 'gmail-view__icon-btn--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowMoreActions(!showMoreActions);
            }}
            aria-label="More actions"
          >
            <MoreVertical size={20} />
          </button>

          {showMoreActions && (
            <div className="gmail-view__more-menu more-actions-dropdown" onClick={(e) => e.stopPropagation()}>
              {isMobile && (
                <button type="button" className="gmail-view__more-item" onClick={() => { onReplyAll(); closeMoreMenu(); }}>
                  <ReplyAll size={16} />
                  Reply all
                </button>
              )}
              <button type="button" className="gmail-view__more-item" onClick={() => { onSendCopy(); closeMoreMenu(); }} disabled={isSendingCopy}>
                <Copy size={16} />
                Send a copy
                {isSendingCopy && <Loader2 size={14} className="animate-spin" />}
              </button>
              <button type="button" className="gmail-view__more-item" onClick={() => { onShowOriginal(); closeMoreMenu(); }} disabled={isShowingOriginal}>
                <Code size={16} />
                Show original
                {isShowingOriginal && <Loader2 size={14} className="animate-spin" />}
              </button>
              <button type="button" className="gmail-view__more-item" onClick={() => { onViewHtml(); closeMoreMenu(); }} disabled={isViewing}>
                <FileSearch size={16} />
                View HTML
                {isViewing && <Loader2 size={14} className="animate-spin" />}
              </button>
              <button type="button" className="gmail-view__more-item" onClick={() => { onPrint(); closeMoreMenu(); }} disabled={isPrinting}>
                <Printer size={16} />
                Print
                {isPrinting && <Loader2 size={14} className="animate-spin" />}
              </button>
              <button type="button" className="gmail-view__more-item" onClick={() => { onDownload(); closeMoreMenu(); }} disabled={isDownloading}>
                <Download size={16} />
                Download
                {isDownloading && <Loader2 size={14} className="animate-spin" />}
              </button>
              <button type="button" className="gmail-view__more-item gmail-view__more-item--danger" onClick={() => { onDelete(); closeMoreMenu(); }}>
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="gmail-view__scroll">
        <div className="gmail-view__content">
          <div className="gmail-view__subject-row">
            <h1 className="gmail-view__subject">{email.envelope.subject || '(no subject)'}</h1>
          </div>

          <article className="gmail-view__message">
            <header className="gmail-view__message-header">
              <div className="gmail-view__avatar" style={{ background: avatarColor }}>
                {initials}
              </div>

              <div className="gmail-view__sender-block">
                <div className="gmail-view__sender-row">
                  <span className="gmail-view__sender-name">{senderName}</span>
                  <span className="gmail-view__sender-email">&lt;{senderAddress}&gt;</span>
                </div>
                <button
                  type="button"
                  className="gmail-view__recipient-toggle"
                  onClick={() => setShowDetails((prev) => !prev)}
                >
                  to {recipientSummary}
                  {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>

              <div className="gmail-view__meta-actions">
                <span className="gmail-view__date" title={formatDate(email.envelope.date)}>
                  {formatDateShort(email.envelope.date)}
                </span>
                <button
                  type="button"
                  className={`gmail-view__icon-btn ${isStarred ? 'gmail-view__icon-btn--starred' : ''}`}
                  onClick={onStar}
                  aria-label={isStarred ? 'Unstar' : 'Star'}
                >
                  <Star size={18} fill={isStarred ? 'currentColor' : 'none'} />
                </button>
                <button type="button" className="gmail-view__icon-btn" onClick={onReply} aria-label="Reply">
                  <Reply size={18} />
                </button>
                <button type="button" className="gmail-view__icon-btn" onClick={onForward} aria-label="Forward">
                  <Forward size={18} />
                </button>
              </div>
            </header>

            {showDetails && (
              <div className="gmail-view__details">
                <div className="gmail-view__details-row">
                  <span className="gmail-view__details-label">from:</span>
                  <span className="gmail-view__details-value">
                    <a href={`mailto:${senderAddress}`}>{formatRecipient(sender)}</a>
                  </span>
                </div>
                <div className="gmail-view__details-row">
                  <span className="gmail-view__details-label">to:</span>
                  <span className="gmail-view__details-value">{formatRecipientsList(email.envelope.to || [])}</span>
                </div>
                {email.envelope.cc && email.envelope.cc.length > 0 && (
                  <div className="gmail-view__details-row">
                    <span className="gmail-view__details-label">cc:</span>
                    <span className="gmail-view__details-value">{formatRecipientsList(email.envelope.cc)}</span>
                  </div>
                )}
                {email.envelope.bcc && email.envelope.bcc.length > 0 && (
                  <div className="gmail-view__details-row">
                    <span className="gmail-view__details-label">bcc:</span>
                    <span className="gmail-view__details-value">{formatRecipientsList(email.envelope.bcc)}</span>
                  </div>
                )}
                <div className="gmail-view__details-row">
                  <span className="gmail-view__details-label">date:</span>
                  <span className="gmail-view__details-value">{formatDate(email.envelope.date)}</span>
                </div>
              </div>
            )}

            <div className="gmail-view__body">
              {renderable.isLoading ? (
                <div className="gmail-view__loading-shimmer">
                  {[100, 100, 100, 55].map((width, index) => (
                    <div key={index} className="gmail-view__shimmer-line" style={{ width: `${width}%` }} />
                  ))}
                </div>
              ) : !renderable.isEmpty ? (
                <iframe
                  ref={iframeRef}
                  title="Email content"
                  srcDoc={`
                    <html>
                      <head>
                        <style>
                          html, body {
                            margin: 0;
                            padding: 0;
                            font-family: 'Google Sans', Roboto, Arial, sans-serif;
                            font-size: 14px;
                            line-height: 1.6;
                            color: #202124;
                          }
                          img { max-width: 100%; height: auto; }
                          table { max-width: 100%; }
                          p, pre, code { white-space: pre-wrap; word-wrap: break-word; }
                          a { color: #1a73e8; text-decoration: none; }
                          a:hover { text-decoration: underline; }
                          blockquote {
                            margin: 0 0 0 0.8ex;
                            border-left: 1px #ccc solid;
                            padding-left: 1ex;
                            color: #5f6368;
                          }
                        </style>
                      </head>
                      <body>${renderable.html}</body>
                    </html>
                  `}
                  onLoad={resizeIframe}
                />
              ) : (
                <p style={{ color: '#5f6368', fontStyle: 'italic', margin: 0 }}>No content</p>
              )}
            </div>

            {email.attachments.length > 0 && (
              <div className="gmail-view__attachments">
                <div className="gmail-view__attachments-label">
                  <Paperclip size={16} />
                  {email.attachments.length} attachment{email.attachments.length === 1 ? '' : 's'}
                </div>
                <div className="gmail-view__attachment-grid">
                  {email.attachments.map((attachment, index) => (
                    <AttachmentItem
                      key={attachment.id || index}
                      fileName={attachment.filename}
                      fileSize={formatFileSize(attachment.size)}
                      contentType={attachment.content_type}
                      public_url={attachment.public_url}
                      onOpen={() => openAttachmentPreview({
                        id: attachment.id,
                        filename: attachment.filename,
                        content_type: attachment.content_type,
                        size: attachment.size,
                        public_url: attachment.public_url,
                      })}
                      onDownload={(progress) => onAttachmentDownload(attachment, progress)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="gmail-view__actions">
              <button type="button" className="gmail-view__action-btn" onClick={onReply}>
                <Reply size={18} />
                Reply
              </button>
              <button type="button" className="gmail-view__action-btn" onClick={onReplyAll}>
                <ReplyAll size={18} />
                Reply all
              </button>
              <button type="button" className="gmail-view__action-btn" onClick={onForward}>
                <Forward size={18} />
                Forward
              </button>
            </div>
          </article>

          {(replies.length > 0 || (totalReplies ?? email.replyCount ?? 0) > 0) && (
            <div className="gmail-view__thread">
              <EmailRepliesThread
                replies={replies}
                formatFileSize={formatFileSize}
                onPreviewAttachment={openAttachmentPreview}
                loading={repliesLoading}
                hasMore={hasMore}
                onLoadMore={loadMore}
                totalReplies={totalReplies ?? (replies.length || email.replyCount || 0)}
                bodyFallback={bodyFallback}
              />
            </div>
          )}
        </div>
      </div>
      <AttachmentPreviewModal
        attachment={previewAttachment}
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        fetchBlob={fetchAttachmentBlob}
        onDownload={onAttachmentDownload}
        formatFileSize={formatFileSize}
      />
    </div>
  );
};

export default EmailViewPanel;
